import React, { useEffect, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronDown, ChevronLeft, X, Trash2,
  Upload, Camera, Video, AlertCircle, Save,
  Package, Plus, Check, BookOpen,
  Sparkles, Shirt, Cpu, ToyBrick, Gamepad2, Trophy,
  Palette, Plane, Car, Wrench, PawPrint, Armchair,
  ChefHat, Luggage, Wheat, Gift, ShoppingBasket,
  Users, UserCheck, Baby, HeartHandshake,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import {
  upsertProduct,
  upsertProductMedia,
  deleteProductPhoto,
  listenToProductData,
} from "../../redux/productSlice";
import { listenToUserCommercePartners } from "../../redux/commercePartnersSlice";

/* ═══════════════════════════════════════════════════════════════
   FASHION — grown-up ranges only. Baby/Maternity lives as its own
   top-level vertical (see BabyCategories import below).
═══════════════════════════════════════════════════════════════ */
import {
  FashionCategories,
  FashionClothing,
  FashionAccessories,
  FashionSunglasses,
  FashionHats,
  FashionWatches,
  FashionJewellery,
  FashionNecklaces,
  FashionRings,
  FashionShoes,
  FashionBags,
  AllFashionProducts,
} from "../../CATEGORIES/FashionCategories";

/* ═══════════════════════════════════════════════════════════════
   BEAUTY — every export from BeautyCategories.tsx
═══════════════════════════════════════════════════════════════ */
import {
  BeautyCategories,
  BeautySkincareSubcategories,
  BeautySkincareFace,
  BeautySunCare,
  BeautyMakeupSubcategories,
  BeautyMakeupFace,
  BeautyMakeupEyes,
  BeautyMakeupLips,
  BeautyHaircareSubcategories,
  BeautyHaircareWash,
  BeautyHairTools,
  BeautyFragranceSubcategories,
  BeautyHomeFragrance,
  BeautyBodySubcategories,
  BeautyNailsSubcategories,
  BeautyToolsSubcategories,
  BeautyMensGroomingSubcategories,
  BeautyWellnessSubcategories,
  BeautyKidsBabySubcategories,
  BeautySetsSubcategories,
  AllBeautyProducts,
} from "../../CATEGORIES/BeautyCategories";

/* ═══════════════════════════════════════════════════════════════
   FARM / GIFTS / GROCERY — full exports each
═══════════════════════════════════════════════════════════════ */
import {
  farmCategories, farmSubcategories, farmProducts,
  farmUnits, farmVariants, renderFarmIcon,
} from "../../CATEGORIES/FarmCategories";
import {
  giftsCategories, giftsSubcategories, giftsProducts,
  giftsOccasions, giftsPriceRanges, renderGiftsIcon,
} from "../../CATEGORIES/GiftsCategories";
import {
  groceryCategories, grocerySubcategories, groceryProducts,
  groceryUnits, groceryVariants, renderGroceryIcon,
} from "../../CATEGORIES/GroceryCategories";

/* ═══════════════════════════════════════════════════════════════
   MATERNITY — the FULL Baby & Maternity tree, standalone. Nothing
   stripped, nothing shared with Fashion.
═══════════════════════════════════════════════════════════════ */
import {
  BabyMaternityCategories,
  BabyAgeGroups,
  BabyClothing as MaternityBabyClothing,
  BabyShoes as MaternityBabyShoes,
  BabyCare as MaternityBabyCare,
  MommyCare as MaternityMommyCare,
  BabyFeeding as MaternityBabyFeeding,
  BabyBedtime as MaternityBabyBedtime,
  BabyTravel as MaternityBabyTravel,
  BabyToys as MaternityBabyToys,
  BabyNursery as MaternityBabyNursery,
  Maternity as MaternityWear,
  BabyHealthSafety as MaternityHealthSafety,
  ActivityEntertainment as MaternityActivity,
  BabyAccessories as MaternityAccessories,
  PottyTraining as MaternityPottyTraining,
  Gifts as MaternityGifts,
  BabyStationery as MaternityStationery,
  CarSeatsAccessories as MaternityCarSeats,
  Clothing as MaternityClothingRange,
  BabyTeething as MaternityTeething,
  AllBabyMaternityProducts,
} from "../../CATEGORIES/BabyCategories";

/* ── All remaining category groups (unchanged) ──────────────── */
import { electronicsCategories, electronicsSubcategories, electronicsProducts }      from "../../CATEGORIES/ElectronicsCategories";
import { idToysCategories, idToysSubcategories, idToysProducts }                    from "../../CATEGORIES/ToysCategories";
import { idGamingCategories, idGamingSubcategories, idGamingProducts }              from "../../CATEGORIES/GamingCategories";
import { sportsCategories, sportsSubcategories, sportsProducts }                    from "../../CATEGORIES/SportsCategories";
import { idArtCraftsCategories, idArtCraftsSubcategories, idArtCraftsProducts }     from "../../CATEGORIES/ArtCraftsCategories";
import { idAviationCategories, idAviationSubcategories, idAviationProducts }        from "../../CATEGORIES/AviationCategories";
import { automotiveCategories, automotiveSubcategories, automotiveProducts }        from "../../CATEGORIES/AutomotiveCategories";
import { toolsCategories, toolsSubcategories, toolsProducts }                       from "../../CATEGORIES/ToolsCategories";
import { idPetsCategories, idPetsSubcategories, idPetsProducts }                    from "../../CATEGORIES/PetsCategories";
import { idFurnitureCategories, idFurnitureSubcategories, idFurnitureProducts }     from "../../CATEGORIES/FurnitureCategories";
import { kitchenCategories, kitchenSubcategories, kitchenProducts }                 from "../../CATEGORIES/KitchenCategories";
import { idLuggageCategories, idLuggageSubcategories, idLuggageProducts }           from "../../CATEGORIES/LuggageCategories";
import { idBooksCategories, idBooksSubcategories, idBooksProducts }                 from "../../CATEGORIES/BooksCategories";

import {
  type AttributeItem,
  fashionAttributes,
  productColors,
  productColorAttributes,
  GENERAL_COLOR_ATTRIBUTE_ID,
  ELECTRONICS_CATEGORY_IDS,

  tvScreenSizeAttributes, tvResolutionAttributes, tvDisplayTypeAttributes, tvSmartAttributes,
  phoneScreenSizeAttributes, phoneResolutionAttributes, phoneDisplayTypeAttributes, phoneStorageAttributes, phoneRamAttributes,
  laptopScreenSizeAttributes, laptopResolutionAttributes, laptopDisplayTypeAttributes, laptopStorageAttributes, laptopRamAttributes, laptopProcessorAttributes,
  tabletScreenSizeAttributes, tabletResolutionAttributes, tabletDisplayTypeAttributes, tabletStorageAttributes, tabletRamAttributes, tabletConnectivityAttributes,
  smartwatchCaseSizeAttributes, smartwatchDisplayTypeAttributes, smartwatchConnectivityAttributes, smartwatchBandMaterialAttributes, smartwatchBatteryLifeAttributes,
  cameraTypeAttributes, cameraMegapixelsAttributes, cameraSensorTypeAttributes, cameraLensMountAttributes, cameraVideoResolutionAttributes,
  headphonesFormFactorAttributes, headphonesConnectivityAttributes, headphonesNoiseControlAttributes, headphonesBatteryLifeAttributes,
  consoleGenerationAttributes, consoleStorageAttributes, consoleEditionAttributes,
  electronicsBrandAttributes, electronicsWarrantyAttributes, electronicsConditionAttributes,

  SkinTypes, SkinConcerns, SkinTones, Undertones, Coverage, Finish,
  HairTypes, HairConcerns, FragranceFamilies, FragranceConcentrations,
  KeyIngredients, BeautyFormats, BeautySustainability, SpfLevels,
  BeautySizes, BeautyBrands,
} from "../../CATEGORIES/ATTRIBUTES/ProductsAttributes";

/* ═══════════════════════════════════════════════════════════════
   DERIVED TREES — Fashion & Beauty (adult ranges, unchanged shape)
═══════════════════════════════════════════════════════════════ */
const idFashionSubcategories = [
  ...FashionClothing,
  ...FashionAccessories,
  ...FashionShoes,
  ...FashionBags,
];
const idFashionProducts = [
  ...FashionSunglasses,
  ...FashionHats,
  ...FashionWatches,
  ...FashionJewellery,
  ...FashionNecklaces,
  ...FashionRings,
];
const idFashionCategories = FashionCategories;

const idBeautySubcategories = [
  ...BeautySkincareSubcategories,
  ...BeautyMakeupSubcategories,
  ...BeautyHaircareSubcategories,
  ...BeautyFragranceSubcategories,
  ...BeautyBodySubcategories,
  ...BeautyNailsSubcategories,
  ...BeautyToolsSubcategories,
  ...BeautyMensGroomingSubcategories,
  ...BeautyWellnessSubcategories,
  ...BeautyKidsBabySubcategories,
  ...BeautySetsSubcategories,
];
const idBeautyLeafProducts = [
  ...BeautySkincareFace, ...BeautySunCare, ...BeautyMakeupFace, ...BeautyMakeupEyes,
  ...BeautyMakeupLips, ...BeautyHaircareWash, ...BeautyHairTools, ...BeautyHomeFragrance,
];
const idBeautyProducts   = idBeautyLeafProducts.length > 0 ? idBeautyLeafProducts : AllBeautyProducts;
const idBeautyCategories = BeautyCategories;

/* ═══════════════════════════════════════════════════════════════
   DERIVED TREE — Maternity (full, unstripped)
═══════════════════════════════════════════════════════════════ */
const idMaternitySubcategories = [
  ...MaternityBabyClothing, ...MaternityBabyShoes, ...MaternityBabyCare, ...MaternityMommyCare,
  ...MaternityBabyFeeding, ...MaternityBabyBedtime, ...MaternityBabyTravel, ...MaternityBabyToys,
  ...MaternityBabyNursery, ...MaternityWear, ...MaternityHealthSafety, ...MaternityActivity,
  ...MaternityAccessories, ...MaternityPottyTraining, ...MaternityGifts, ...MaternityStationery,
  ...MaternityCarSeats, ...MaternityClothingRange, ...MaternityTeething,
];
const idMaternityProducts   = AllBabyMaternityProducts;
const idMaternityCategories = BabyMaternityCategories;

/* Age Group attribute — Maternity only, from BabyAgeGroups */
const maternityAgeGroupAttribute: AttributeItem = { id: 90301, name: "Age Group", values: BabyAgeGroups as any };
const maternityAttributeGroups: AttributeItem[] = [maternityAgeGroupAttribute];

/* ═══════════════════════════════════════════════════════════════
   BEAUTY PRODUCT ATTRIBUTE GROUPS
═══════════════════════════════════════════════════════════════ */
const beautyAttributes: AttributeItem[] = [
  { id: 39001, name: "Skin Type",              values: SkinTypes },
  { id: 39002, name: "Skin Concern",           values: SkinConcerns },
  { id: 39003, name: "Skin Tone",              values: SkinTones },
  { id: 39004, name: "Undertone",              values: Undertones },
  { id: 39005, name: "Coverage",               values: Coverage },
  { id: 39006, name: "Finish",                 values: Finish },
  { id: 39007, name: "Hair Type",              values: HairTypes },
  { id: 39008, name: "Hair Concern",           values: HairConcerns },
  { id: 39009, name: "Fragrance Family",       values: FragranceFamilies },
  { id: 39010, name: "Fragrance Concentration",values: FragranceConcentrations },
  { id: 39011, name: "Key Ingredients",        values: KeyIngredients },
  { id: 39012, name: "Format",                 values: BeautyFormats },
  { id: 39013, name: "Sustainability",         values: BeautySustainability },
  { id: 39014, name: "SPF Level",              values: SpfLevels },
  { id: 39015, name: "Size",                   values: BeautySizes },
  { id: 39016, name: "Brand",                  values: BeautyBrands },
];

/* ═══════════════════════════════════════════════════════════════
   FARM / GIFTS / GROCERY — domain-specific groups
═══════════════════════════════════════════════════════════════ */
const farmAttributeGroups: AttributeItem[] = [
  { id: 90001, name: "Unit",              values: farmUnits as any },
  { id: 90002, name: "Quality / Variant", values: farmVariants as any, multiSelect: true } as any,
];
const giftsAttributeGroups: AttributeItem[] = [
  { id: 90101, name: "Occasion",    values: giftsOccasions as any, multiSelect: true } as any,
  { id: 90102, name: "Price Range", values: giftsPriceRanges as any },
];
const groceryAttributeGroups: AttributeItem[] = [
  { id: 90201, name: "Unit",    values: groceryUnits as any },
  { id: 90202, name: "Variant", values: groceryVariants as any, multiSelect: true } as any,
];

/* ═══════════════════════════════════════════════════════════════
   COLOR
═══════════════════════════════════════════════════════════════ */
function resolveColorGroup(): AttributeItem | undefined {
  if (productColorAttributes?.values?.length) return productColorAttributes;
  return productColors.find((g) => g.id === GENERAL_COLOR_ATTRIBUTE_ID);
}

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS
═══════════════════════════════════════════════════════════════ */
const T = {
  bg:           "#FFFFFF",
  bgSoft:       "#FCEFD4",
  bgMuted:      "#F3E6CE",
  pageBg:       "#FBF6EE",
  text:         "#1B0E06",
  textSoft:     "#1B0E06",
  muted:        "#7A6653",
  mutedDim:     "#A6947F",
  gold:         "#F2A93E",
  goldLight:    "#F7C874",
  red:          "#C8202E",
  orange:       "#D4900F",
  dark:         "#1B0E06",
  border:       "rgba(242,169,62,0.22)",
  borderOrange: "rgba(200,32,46,0.35)",
  white:        "#FFFFFF",
  green:        "#1E8E5A",
};
const GRAD      = "linear-gradient(90deg,#1B0E06 0%,#F2A93E 35%,#C8202E 65%,#9E1521 100%)";
const GRAD_SAVE = "linear-gradient(90deg,#F2A93E 0%,#C8202E 50%,#9E1521 100%)";
const GRAD_SIZE = "300% 100%";

/* ═══════════════════════════════════════════════════════════════
   CATEGORY GROUPS — Maternity added, Fast Food excluded
═══════════════════════════════════════════════════════════════ */
const GROUP_ICON: Record<string, React.ReactNode> = {
  Beauty:         <Sparkles size={15} />,
  Fashion:        <Shirt size={15} />,
  Maternity:      <HeartHandshake size={15} />,
  Electronics:    <Cpu size={15} />,
  Toys:           <ToyBrick size={15} />,
  Gaming:         <Gamepad2 size={15} />,
  Sports:         <Trophy size={15} />,
  "Art & Crafts": <Palette size={15} />,
  Aviation:       <Plane size={15} />,
  Automotive:     <Car size={15} />,
  Tools:          <Wrench size={15} />,
  Pets:           <PawPrint size={15} />,
  Furniture:      <Armchair size={15} />,
  Kitchen:        <ChefHat size={15} />,
  Luggage:        <Luggage size={15} />,
  Books:          <BookOpen size={15} />,
  Farm:           <Wheat size={15} />,
  Gifts:          <Gift size={15} />,
  Grocery:        <ShoppingBasket size={15} />,
};

const CATEGORY_GROUPS = [
  { name: "Beauty",      categories: idBeautyCategories,     subs: idBeautySubcategories,   products: idBeautyProducts     },
  { name: "Fashion",     categories: idFashionCategories,    subs: idFashionSubcategories,  products: idFashionProducts    },
  { name: "Maternity",   categories: idMaternityCategories,  subs: idMaternitySubcategories,products: idMaternityProducts  },
  { name: "Electronics", categories: electronicsCategories,  subs: electronicsSubcategories,products: electronicsProducts  },
  { name: "Toys",        categories: idToysCategories,       subs: idToysSubcategories,     products: idToysProducts       },
  { name: "Gaming",      categories: idGamingCategories,     subs: idGamingSubcategories,   products: idGamingProducts     },
  { name: "Sports",      categories: sportsCategories,       subs: sportsSubcategories,     products: sportsProducts       },
  { name: "Art & Crafts",categories: idArtCraftsCategories,  subs: idArtCraftsSubcategories,products: idArtCraftsProducts  },
  { name: "Aviation",    categories: idAviationCategories,   subs: idAviationSubcategories, products: idAviationProducts   },
  { name: "Automotive",  categories: automotiveCategories,   subs: automotiveSubcategories, products: automotiveProducts   },
  { name: "Tools",       categories: toolsCategories,        subs: toolsSubcategories,      products: toolsProducts        },
  { name: "Pets",        categories: idPetsCategories,       subs: idPetsSubcategories,     products: idPetsProducts       },
  { name: "Furniture",   categories: idFurnitureCategories,  subs: idFurnitureSubcategories,products: idFurnitureProducts  },
  { name: "Kitchen",     categories: kitchenCategories,      subs: kitchenSubcategories,    products: kitchenProducts      },
  { name: "Luggage",     categories: idLuggageCategories,    subs: idLuggageSubcategories,  products: idLuggageProducts    },
  { name: "Books",       categories: idBooksCategories,      subs: idBooksSubcategories,    products: idBooksProducts      },
  { name: "Farm",        categories: farmCategories,         subs: farmSubcategories,       products: farmProducts         },
  { name: "Gifts",       categories: giftsCategories,        subs: giftsSubcategories,      products: giftsProducts        },
  { name: "Grocery",     categories: groceryCategories,      subs: grocerySubcategories,    products: groceryProducts      },
];

// Maps storeType ids (subcategories of businessType id 12 "Stores") to CATEGORY_GROUPS names.
// NOTE: adjust ids to match whatever your onboarding flow actually assigns.
const STORE_TYPE_TO_GROUP: Record<number, string> = {
  1208: "Fashion",
  1209: "Beauty",
  1210: "Maternity",
  1211: "Toys",
  1212: "Gaming",
  1213: "Sports",
  1214: "Art & Crafts",
  1215: "Aviation",
  1216: "Automotive",
  1217: "Tools",
  1218: "Pets",
  1219: "Furniture",
  1220: "Kitchen",
  1221: "Luggage",
  1222: "Books",
  1223: "Electronics",
  1224: "Farm",
  1225: "Gifts",
  1226: "Grocery",
};
const BUSINESS_TYPE_TO_GROUP: Record<number, string> = {
  11: "Electronics",
};

const MAX_IMAGES = 10;

const ELECTRONICS_DEVICE_GROUPS: Record<number, AttributeItem[]> = {
  [ELECTRONICS_CATEGORY_IDS.TV]: [tvScreenSizeAttributes, tvResolutionAttributes, tvDisplayTypeAttributes, tvSmartAttributes],
  [ELECTRONICS_CATEGORY_IDS.PHONE]: [phoneScreenSizeAttributes, phoneResolutionAttributes, phoneDisplayTypeAttributes, phoneStorageAttributes, phoneRamAttributes],
  [ELECTRONICS_CATEGORY_IDS.LAPTOP]: [laptopScreenSizeAttributes, laptopResolutionAttributes, laptopDisplayTypeAttributes, laptopStorageAttributes, laptopRamAttributes, laptopProcessorAttributes],
  [ELECTRONICS_CATEGORY_IDS.TABLET]: [tabletScreenSizeAttributes, tabletResolutionAttributes, tabletDisplayTypeAttributes, tabletStorageAttributes, tabletRamAttributes, tabletConnectivityAttributes],
  [ELECTRONICS_CATEGORY_IDS.SMARTWATCH]: [smartwatchCaseSizeAttributes, smartwatchDisplayTypeAttributes, smartwatchConnectivityAttributes, smartwatchBandMaterialAttributes, smartwatchBatteryLifeAttributes],
  [ELECTRONICS_CATEGORY_IDS.CAMERA]: [cameraTypeAttributes, cameraMegapixelsAttributes, cameraSensorTypeAttributes, cameraLensMountAttributes, cameraVideoResolutionAttributes],
  [ELECTRONICS_CATEGORY_IDS.HEADPHONES]: [headphonesFormFactorAttributes, headphonesConnectivityAttributes, headphonesNoiseControlAttributes, headphonesBatteryLifeAttributes],
  [ELECTRONICS_CATEGORY_IDS.GAMING_CONSOLE]: [consoleGenerationAttributes, consoleStorageAttributes, consoleEditionAttributes],
};
const commonElectronicsGroups: AttributeItem[] = [electronicsBrandAttributes, electronicsWarrantyAttributes, electronicsConditionAttributes];

/* ═══════════════════════════════════════════════════════════════
   GLOBAL CSS
═══════════════════════════════════════════════════════════════ */
const CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700;800;900&family=DM+Sans:opsz,wght@9..40,300;9..40,400;9..40,500;9..40,600;9..40,700&display=swap');
  *,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
  ::-webkit-scrollbar{width:4px;height:4px;}
  ::-webkit-scrollbar-track{background:transparent;}
  ::-webkit-scrollbar-thumb{background:rgba(242,169,62,0.25);border-radius:99px;}
  input,button,select,textarea{font-family:'DM Sans',sans-serif;}
  @keyframes arSpin{0%{background-position:0% 50%;}50%{background-position:100% 50%;}100%{background-position:0% 50%;}}
  @keyframes fadeUp{from{opacity:0;transform:translateY(12px);}to{opacity:1;transform:translateY(0);}}
  @keyframes shimmer{0%{background-position:-200% center;}100%{background-position:200% center;}}
  @keyframes pulse{0%,100%{opacity:1;}50%{opacity:.5;}}
  @keyframes floatIn{from{opacity:0;transform:translateY(-8px) scale(.97);}to{opacity:1;transform:translateY(0) scale(1);}}
  @keyframes stepIn{from{opacity:0;transform:translateY(6px);}to{opacity:1;transform:translateY(0);}}
  input::placeholder,textarea::placeholder{color:#A6947F;}
  .ap-input:focus{outline:none;border-color:rgba(200,32,46,.55)!important;box-shadow:0 0 0 3px rgba(200,32,46,.1)!important;}
  textarea.ap-input:focus{outline:none;border-color:rgba(200,32,46,.55)!important;box-shadow:0 0 0 3px rgba(200,32,46,.1)!important;}

  .ap-form-card{
    background:#fff;
    border:1.5px solid rgba(242,169,62,0.22);
    border-radius:20px;
    padding:26px 24px;
    animation:fadeUp .32s ease both;
    box-shadow:0 2px 20px rgba(242,169,62,0.07);
    transition:border-color .2s,box-shadow .2s;
  }
  .ap-form-card:hover{border-color:rgba(200,32,46,.16);box-shadow:0 4px 28px rgba(242,169,62,0.11);}
  .ap-form-card::before{
    content:'';display:block;height:3px;
    background:linear-gradient(90deg,#1B0E06 0%,#F2A93E 35%,#C8202E 65%,#9E1521 100%);
    background-size:300% 100%;animation:arSpin 6s linear infinite;
    border-radius:20px 20px 0 0;margin:-26px -24px 22px;
  }
  .ap-step{ animation:stepIn .28s ease both; }
  .ap-step + .ap-step{ margin-top:26px; padding-top:26px; border-top:1px solid rgba(242,169,62,0.18); }
  .ap-step-head{ display:flex; align-items:center; gap:10px; margin-bottom:14px; }
  .ap-step-num{
    width:24px;height:24px;border-radius:8px;flex-shrink:0;
    display:flex;align-items:center;justify-content:center;
    background:linear-gradient(135deg,#F2A93E,#C8202E);
    color:#fff;font-size:11px;font-weight:900;font-family:'Sora',sans-serif;
  }

  .ac{padding:6px 14px;border-radius:8px;font-size:12px;font-weight:600;cursor:pointer;border:1.5px solid rgba(242,169,62,0.22);background:#FCEFD4;color:#7A6653;transition:all .15s;font-family:'DM Sans',sans-serif;}
  .ac:hover{border-color:rgba(200,32,46,.4);color:#C8202E;background:rgba(200,32,46,.05);}
  .ac.on{background:linear-gradient(90deg,#F2A93E 0%,#C8202E 50%,#9E1521 100%);color:#fff;border-color:transparent;box-shadow:0 2px 10px rgba(200,32,46,.3);}
  .gc{display:inline-flex;align-items:center;gap:7px;padding:8px 16px;border-radius:10px;font-size:13px;font-weight:700;cursor:pointer;border:1.5px solid rgba(242,169,62,0.22);background:#FCEFD4;color:#1B0E06;transition:all .15s;font-family:'DM Sans',sans-serif;white-space:nowrap;}
  .gc svg{flex-shrink:0;}
  .gc:disabled{cursor:not-allowed;}
  .gc:disabled:hover{border-color:rgba(242,169,62,0.22);background:#FCEFD4;}
  .gc:hover{border-color:rgba(200,32,46,.4);background:rgba(200,32,46,.05);}
  .gc.on{background:linear-gradient(90deg,#F2A93E 0%,#C8202E 50%,#9E1521 100%);color:#fff;border-color:transparent;box-shadow:0 2px 10px rgba(200,32,46,.3);}
  .ttrack{width:48px;height:27px;border-radius:14px;border:none;cursor:pointer;position:relative;transition:background .22s;flex-shrink:0;}
  .tthumb{position:absolute;top:3.5px;width:20px;height:20px;border-radius:50%;background:#fff;transition:left .22s;box-shadow:0 1px 6px rgba(0,0,0,.25);}
  .media-thumb{width:82px;height:100px;position:relative;overflow:hidden;border-radius:12px;border:1.5px solid rgba(242,169,62,0.22);transition:transform .2s,box-shadow .2s;flex-shrink:0;}
  .media-thumb:hover{transform:translateY(-2px);box-shadow:0 6px 18px rgba(200,32,46,.18);}
  .add-media{width:82px;height:100px;border:1.5px dashed rgba(200,32,46,.4);border-radius:12px;background:rgba(200,32,46,.04);display:flex;flex-direction:column;align-items:center;justify-content:center;cursor:pointer;gap:5px;transition:border-color .2s,background .2s;flex-shrink:0;}
  .add-media:hover{border-color:rgba(200,32,46,.7);background:rgba(200,32,46,.09);}
  .sec-label{font-size:11px;font-weight:800;letter-spacing:.18em;background:linear-gradient(90deg,#1B0E06 0%,#F2A93E 40%,#C8202E 70%,#1B0E06 100%);background-size:200% auto;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:shimmer 5s linear infinite;}
  .variant-card{border:1.5px solid rgba(242,169,62,0.22);border-radius:14px;background:#FCEFD4;padding:14px;transition:border-color .2s,box-shadow .2s;}
  .variant-card:hover{border-color:rgba(200,32,46,.3);box-shadow:0 4px 16px rgba(200,32,46,.09);}
  .vis-row{display:flex;align-items:center;justify-content:space-between;padding:14px 0;border-bottom:1px solid rgba(242,169,62,0.15);}
  .vis-row:last-child{border-bottom:none;padding-bottom:0;}
  .save-btn{display:flex;align-items:center;justify-content:center;gap:9px;border:none;border-radius:14px;padding:16px 0;width:100%;font-family:'Sora',sans-serif;font-weight:800;font-size:15px;cursor:pointer;transition:all .2s;letter-spacing:.03em;}
  .save-btn.active{background:linear-gradient(90deg,#F2A93E 0%,#C8202E 50%,#9E1521 100%);color:#fff;box-shadow:0 6px 24px rgba(200,32,46,.38);}
  .save-btn.inactive{background:#F3E6CE;color:#A6947F;cursor:not-allowed;}
  .save-btn.active:hover{transform:translateY(-1px);box-shadow:0 8px 28px rgba(200,32,46,.45);}
  .sheet-backdrop{position:fixed;inset:0;background:rgba(27,14,6,.38);z-index:9999;display:flex;align-items:flex-end;}
  .float-backdrop{position:fixed;inset:0;background:rgba(27,14,6,.18);z-index:9999;display:flex;align-items:center;justify-content:center;}
  .float-panel{background:#fff;border-radius:20px;border:1.5px solid rgba(200,32,46,.28);box-shadow:0 24px 80px rgba(27,14,6,.22), 0 4px 24px rgba(200,32,46,.15);overflow:hidden;display:flex;flex-direction:column;min-width:320px;max-width:440px;width:100%;max-height:72vh;animation:floatIn .2s ease both;z-index:10000;}
  .bottom-sheet{background:#fff;width:100%;max-height:70vh;border-top-left-radius:24px;border-top-right-radius:24px;border-top:1.5px solid rgba(200,32,46,.25);overflow:hidden;display:flex;flex-direction:column;}
  .attr-float{position:fixed;background:#fff;border-radius:16px;border:1.5px solid rgba(200,32,46,.28);box-shadow:0 20px 64px rgba(27,14,6,.18), 0 2px 20px rgba(200,32,46,.13);z-index:10000;min-width:280px;max-width:420px;width:320px;max-height:400px;overflow-y:auto;padding:14px 16px 16px;animation:floatIn .18s ease both;}
  .sheet-list{overflow-y:auto;flex:1;}
  @media(min-width:900px){
    .ap-desktop-grid{display:grid;grid-template-columns:1.6fr 1fr;gap:20px;align-items:start;}
    .ap-sidebar-sticky{position:sticky;top:80px;}
  }
  .ap-select{width:100%;border:1.5px solid rgba(242,169,62,0.22);border-radius:10px;background:#FCEFD4;color:#1B0E06;padding:11px 38px 11px 14px;font-size:13px;outline:none;appearance:none;cursor:pointer;transition:border-color .15s;font-family:'DM Sans',sans-serif;}
  .ap-select:focus{border-color:rgba(200,32,46,.55);box-shadow:0 0 0 3px rgba(200,32,46,.1);}
  .ap-select:disabled{opacity:.5;cursor:not-allowed;}
  .color-grid{display:grid;grid-template-columns:repeat(auto-fill,minmax(72px,1fr));gap:10px;padding-top:8px;}
  .color-cell{display:flex;flex-direction:column;align-items:center;gap:6px;cursor:pointer;position:relative;}
  .color-sphere{width:40px;height:40px;border-radius:50%;flex-shrink:0;position:relative;transition:transform .18s,box-shadow .18s;}
  .color-cell:hover .color-sphere{transform:scale(1.12) translateY(-2px);}
  .color-sphere.sel{outline:2.5px solid #C8202E;outline-offset:3px;}
  .color-tick{position:absolute;bottom:-3px;right:-3px;width:16px;height:16px;border-radius:50%;background:#C8202E;display:flex;align-items:center;justify-content:center;box-shadow:0 1px 4px rgba(0,0,0,.3);}
  .color-name{font-size:10px;font-weight:500;color:#7A6653;text-align:center;line-height:1.2;font-family:'DM Sans',sans-serif;max-width:68px;word-break:break-word;transition:color .15s;}
  .color-cell.sel .color-name{color:#C8202E;font-weight:700;}
  .color-sphere-multi{background:conic-gradient(#e53935 0deg 51deg,#FF9800 51deg 102deg,#FFEB3B 102deg 153deg,#228B22 153deg 204deg,#4169E1 204deg 255deg,#9C27B0 255deg 306deg,#e53935 306deg 360deg);box-shadow:inset -6px -4px 12px rgba(0,0,0,0.28),inset 6px 5px 12px rgba(255,255,255,0.18),0 4px 14px rgba(0,0,0,0.22);}
  .color-sphere-transparent{background:repeating-conic-gradient(#d0d0d0 0% 25%,#f8f8f8 0% 50%) 0 0/10px 10px;border:1.5px solid rgba(0,0,0,0.10);box-shadow:inset -4px -3px 8px rgba(0,0,0,0.10),0 3px 10px rgba(0,0,0,0.10);}
  .opt-glyph{width:22px;height:22px;object-fit:contain;flex-shrink:0;border-radius:5px;}
`;

/* ═══════════════════════════════════════════════════════════════
   TYPES
═══════════════════════════════════════════════════════════════ */
interface VariantShape {
  imageUrl?:   string;
  imageFile?:  File;
  previewUrl?: string;
  sizeIds:     number[];
  stock:       number;
}
interface FormState {
  listingId?:    string;
  groupIndex:    number | null;
  categoryId:    number | null;
  subcategoryId: number | null;
  productId:     number | null;
  title:         string;
  description:   string;
  stock:         number;
  rate:          number;
  available:     boolean;
  isPublished:   boolean;
  images:        File[];
  photos:        string[];
  video?:        string | null;
  videoFile?:    File | null;
  attributes:    Record<number, number | number[] | undefined>;
  variants:      Record<number, VariantShape>;
  dimensions?:   { unit: "cm" | "inch"; weightUnit: "g" | "ml"; height?: number; width?: number; length?: number; netWeight?: number };
}
const blank = (): FormState => ({
  groupIndex: null, categoryId: null, subcategoryId: null, productId: null,
  title: "", description: "", stock: 0, rate: 0, available: true, isPublished: false,
  images: [], photos: [], video: null, videoFile: null,
  attributes: {}, variants: {},
  dimensions: { unit: "cm", weightUnit: "g" },
});
const findGroupIndex = (categoryId: number | null) => {
  if (categoryId == null) return null;
  const idx = CATEGORY_GROUPS.findIndex(g => (g.categories as any[]).some((c: any) => c.id === categoryId));
  return idx === -1 ? null : idx;
};
const toggleMulti = (arr: number[] = [], val: number): number[] =>
  arr.includes(val) ? arr.filter(v => v !== val) : [...arr, val];

/* ═══════════════════════════════════════════════════════════════
   UI PRIMITIVES
═══════════════════════════════════════════════════════════════ */
const Toggle: React.FC<{ on: boolean; onChange: (v: boolean) => void }> = ({ on, onChange }) => (
  <button className="ttrack" onClick={() => onChange(!on)} style={{ background: on ? GRAD_SAVE : "#A6947F", backgroundSize: on ? GRAD_SIZE : undefined, animation: on ? "arSpin 4s ease infinite" : "none" }}>
    <div className="tthumb" style={{ left: on ? 25 : 4 }} />
  </button>
);
const StepHead: React.FC<{ n: number; t: string; sub?: string }> = ({ n, t, sub }) => (
  <div className="ap-step-head">
    <div className="ap-step-num">{n}</div>
    <div>
      <span className="sec-label">{t.toUpperCase()}</span>
      {sub && <p style={{ fontSize: 11, color: T.muted, marginTop: 2, fontFamily: "'DM Sans',sans-serif" }}>{sub}</p>}
    </div>
  </div>
);
const SLabel: React.FC<{ t: string; sub?: string }> = ({ t, sub }) => (
  <div style={{ marginBottom: 16 }}>
    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: sub ? 4 : 0 }}>
      <div style={{ width: 3, height: 16, borderRadius: 2, background: GRAD_SAVE, flexShrink: 0 }} />
      <span className="sec-label">{t.toUpperCase()}</span>
    </div>
    {sub && <p style={{ fontSize: 11, color: T.muted, marginTop: 3, marginLeft: 11, fontFamily: "'DM Sans',sans-serif" }}>{sub}</p>}
  </div>
);
const FL: React.FC<{ l: string; req?: boolean }> = ({ l, req }) => (
  <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.15em", color: T.muted, marginBottom: 6, fontFamily: "'Sora',sans-serif" }}>
    {l.toUpperCase()}{req && <span style={{ color: T.orange, marginLeft: 3 }}>*</span>}
  </div>
);
const NIn: React.FC<{
  value: string | number; onChange: (v: string) => void;
  placeholder?: string; type?: string; rows?: number;
  prefix?: string; disabled?: boolean; suffix?: string;
}> = ({ value, onChange, placeholder, type = "text", rows, prefix, disabled, suffix }) => (
  <div style={{ display: "flex", border: `1.5px solid ${T.border}`, borderRadius: 10, background: disabled ? T.bgSoft : T.white, overflow: "hidden", opacity: disabled ? 0.55 : 1, transition: "border-color .15s" }}>
    {prefix && <div style={{ padding: "0 13px", display: "flex", alignItems: "center", background: T.bgSoft, borderRight: `1px solid ${T.border}`, fontSize: 13, color: T.gold, fontWeight: 700, flexShrink: 0 }}>{prefix}</div>}
    {rows
      ? <textarea className="ap-input" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows} disabled={disabled}
          style={{ flex: 1, border: "none", outline: "none", padding: "11px 14px", fontSize: 13, color: T.text, background: "transparent", resize: "vertical", lineHeight: 1.65, fontFamily: "'DM Sans',sans-serif" }} />
      : <input className="ap-input" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} type={type} disabled={disabled}
          style={{ flex: 1, border: "none", outline: "none", padding: "11px 14px", fontSize: 13, color: T.text, background: "transparent", fontFamily: "'DM Sans',sans-serif" }} />
    }
    {suffix && <div style={{ padding: "0 13px", display: "flex", alignItems: "center", fontSize: 12, color: T.muted, flexShrink: 0 }}>{suffix}</div>}
  </div>
);

function renderIconValue(icon: any, size = 18, color?: string): React.ReactNode {
  if (!icon) return null;
  if (React.isValidElement(icon)) return icon;
  if (typeof icon === "function") { const Icon = icon; return <Icon size={size} color={color} />; }
  return null;
}

const OptionGlyph: React.FC<{
  icon?: any; image?: any; color?: string; size?: number;
  raw?: any; renderIcon?: (item: any, size: number) => React.ReactNode;
}> = ({ icon, image, color, size = 20, raw, renderIcon }) => {
  const [imgError, setImgError] = useState(false);
  if (image && !imgError) return <img src={image} alt="" className="opt-glyph" onError={() => setImgError(true)} style={{ width: size, height: size }} />;
  if (renderIcon && raw) {
    const domainRendered = renderIcon(raw, size);
    if (domainRendered) return <span style={{ display: "flex", alignItems: "center", opacity: 0.75, flexShrink: 0 }}>{domainRendered}</span>;
  }
  const rendered = renderIconValue(icon, size, color);
  if (rendered) return <span style={{ display: "flex", alignItems: "center", opacity: 0.75, flexShrink: 0 }}>{rendered}</span>;
  return null;
};

const NSel: React.FC<{
  value: number | string | null; onChange: (v: number) => void;
  options: { id: number; name: string; icon?: any; image?: any; color?: string; raw?: any }[];
  placeholder?: string; disabled?: boolean;
  renderIcon?: (item: any, size: number) => React.ReactNode;
}> = ({ value, onChange, options, placeholder = "Select…", disabled, renderIcon }) => {
  const [open, setOpen] = useState(false);
  const [isMob, setIsMob] = useState(window.innerWidth < 900);
  const selected = options.find(o => o.id === value);
  useEffect(() => { const fn = () => setIsMob(window.innerWidth < 900); window.addEventListener("resize", fn); return () => window.removeEventListener("resize", fn); }, []);
  useEffect(() => { if (!open) return; const fn = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); }; window.addEventListener("keydown", fn); return () => window.removeEventListener("keydown", fn); }, [open]);

  const listItems = options.map(opt => (
    <button key={opt.id} onClick={() => { onChange(opt.id); setOpen(false); }}
      style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: isMob ? "14px 20px" : "10px 14px", borderTop: "none", borderLeft: "none", borderRight: "none", borderBottom: `1px solid ${T.border}`, background: value === opt.id ? "rgba(200,32,46,.06)" : "transparent", cursor: "pointer", fontFamily: "'DM Sans',sans-serif", borderRadius: isMob ? 0 : 8 }}>
      <span style={{ display: "flex", alignItems: "center", gap: 10, fontSize: 14, color: value === opt.id ? T.orange : T.text, fontWeight: value === opt.id ? 700 : 400 }}>
        <OptionGlyph icon={opt.icon} image={opt.image} color={opt.color} raw={opt.raw} renderIcon={renderIcon} />
        {opt.name}
      </span>
      {value === opt.id && <Check size={16} color={T.orange} />}
    </button>
  ));
  const desktopModal = (
    <motion.div className="float-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}>
      <motion.div className="float-panel" initial={{ scale: 0.96, opacity: 0, y: -10 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.96, opacity: 0, y: -10 }} transition={{ type: "spring", damping: 26, stiffness: 340 }} onClick={e => e.stopPropagation()}>
        <div style={{ height: 3, background: GRAD, backgroundSize: GRAD_SIZE, animation: "arSpin 5s linear infinite", flexShrink: 0 }} />
        <div style={{ padding: "14px 18px 10px", flexShrink: 0, borderBottom: `1px solid ${T.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <p style={{ fontSize: 14, fontWeight: 800, color: T.text, fontFamily: "'Sora',sans-serif" }}>{placeholder}</p>
          <button onClick={() => setOpen(false)} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex", padding: 4 }}><X size={16} /></button>
        </div>
        <div className="sheet-list" style={{ padding: "8px 10px" }}>{listItems}</div>
      </motion.div>
    </motion.div>
  );
  const mobileSheet = (
    <motion.div className="sheet-backdrop" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setOpen(false)}>
      <motion.div className="bottom-sheet" initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }} transition={{ type: "spring", damping: 28, stiffness: 300 }} onClick={e => e.stopPropagation()}>
        <div style={{ height: 3, background: GRAD, backgroundSize: GRAD_SIZE, animation: "arSpin 5s linear infinite", flexShrink: 0 }} />
        <div style={{ width: 36, height: 4, borderRadius: 2, background: T.mutedDim, margin: "12px auto 8px", flexShrink: 0 }} />
        <p style={{ fontSize: 13, fontWeight: 800, color: T.text, padding: "4px 20px 10px", fontFamily: "'Sora',sans-serif", flexShrink: 0 }}>{placeholder}</p>
        <div className="sheet-list">{listItems}</div>
      </motion.div>
    </motion.div>
  );
  return (
    <>
      <button
        onClick={() => !disabled && setOpen(true)}
        disabled={disabled}
        style={{
          width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
          border: `1.5px solid ${open ? T.borderOrange : T.border}`, borderRadius: 10,
          background: disabled ? T.bgSoft : T.white,
          padding: "11px 14px", fontSize: 13, color: selected ? T.text : T.mutedDim,
          cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.55 : 1,
          fontFamily: "'DM Sans',sans-serif", transition: "border-color .15s",
          boxShadow: open ? "0 0 0 3px rgba(200,32,46,.1)" : "none",
        }}
      >
        <span style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {selected && <OptionGlyph icon={selected.icon} image={selected.image} color={selected.color} raw={selected.raw} renderIcon={renderIcon} size={18} />}
          {selected?.name ?? placeholder}
        </span>
        <ChevronDown size={14} color={T.muted} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s" }} />
      </button>
      {createPortal(
        <AnimatePresence>
          {open && (isMob ? mobileSheet : desktopModal)}
        </AnimatePresence>,
        document.body
      )}
    </>
  );
};

/* ═══════════════════════════════════════════════════════════════
   AttrSheet — Mobile: bottom sheet | Desktop: portal fixed panel
═══════════════════════════════════════════════════════════════ */
const AttrSheet: React.FC<{
  label: string; summary: string; optional?: boolean; disabled?: boolean;
  children: React.ReactNode;
}> = ({ label, summary, optional, disabled, children }) => {
  const [open, setOpen]         = useState(false);
  const [isMob, setIsMob]       = useState(window.innerWidth < 900);
  const [anchorRect, setAnchorRect] = useState<{ top: number; left: number; width: number } | null>(null);
  const anchorRef               = useRef<HTMLDivElement>(null);
  const floatRef                = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const fn = () => setIsMob(window.innerWidth < 900);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  useEffect(() => {
    if (!open || isMob) return;
    const update = () => {
      if (anchorRef.current) {
        const r = anchorRef.current.getBoundingClientRect();
        setAnchorRect({ top: r.bottom + 8, left: r.left, width: r.width });
      }
    };
    update();
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, isMob]);
  useEffect(() => {
    if (!open || isMob) return;
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (anchorRef.current?.contains(t) || floatRef.current?.contains(t)) return;
      setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open, isMob]);
  useEffect(() => {
    if (!open) return;
    const fn = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", fn);
    return () => window.removeEventListener("keydown", fn);
  }, [open]);
  const handleOpen = () => {
    if (disabled) return;
    if (!open && anchorRef.current) {
      const r = anchorRef.current.getBoundingClientRect();
      setAnchorRect({ top: r.bottom + 8, left: r.left, width: r.width });
    }
    setOpen(o => !o);
  };
  useEffect(() => { if (disabled) setOpen(false); }, [disabled]);
  const triggerRow = (
    <button
      onClick={handleOpen}
      disabled={disabled}
      style={{
        width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "13px 0",
        borderTop: "none", borderLeft: "none", borderRight: "none",
        borderBottom: `1px solid ${T.border}`,
        background: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.35 : 1,
        filter: disabled ? "grayscale(1)" : "none",
        transition: "opacity .15s, filter .15s",
      }}
    >
      <div style={{ textAlign: "left" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
          <span style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: "'DM Sans',sans-serif" }}>{label}</span>
          {optional && <span style={{ fontSize: 10, color: T.muted, background: T.bgMuted, borderRadius: 6, padding: "1px 6px" }}>optional</span>}
        </div>
        <p style={{ fontSize: 12, color: T.muted, fontFamily: "'DM Sans',sans-serif" }}>{summary || "Tap to select"}</p>
      </div>
      <ChevronDown size={16} color={T.mutedDim} style={{ transform: open ? "rotate(180deg)" : "none", transition: "transform .2s", flexShrink: 0 }} />
    </button>
  );
  const mobileSheet = (
    <motion.div
      className="sheet-backdrop"
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={() => setOpen(false)}
    >
      <motion.div
        className="bottom-sheet"
        initial={{ y: 60, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 60, opacity: 0 }}
        transition={{ type: "spring", damping: 28, stiffness: 300 }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ height: 3, background: GRAD, backgroundSize: GRAD_SIZE, animation: "arSpin 5s linear infinite", flexShrink: 0 }} />
        <div style={{ width: 36, height: 4, borderRadius: 2, background: T.mutedDim, margin: "12px auto 8px", flexShrink: 0 }} />
        <div style={{ padding: "4px 20px 14px", flexShrink: 0 }}>
          <p style={{ fontSize: 15, fontWeight: 800, color: T.text, fontFamily: "'Sora',sans-serif" }}>{label}</p>
        </div>
        <div className="sheet-list" style={{ padding: "0 20px 24px" }}>{children}</div>
        <div style={{ padding: "12px 20px 20px", flexShrink: 0 }}>
          <button
            onClick={() => setOpen(false)}
            style={{ width: "100%", padding: "13px", borderRadius: 12, border: "none", background: GRAD_SAVE, color: "#fff", fontWeight: 700, fontSize: 14, cursor: "pointer", fontFamily: "'Sora',sans-serif" }}
          >
            Done
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
  const desktopFloat = anchorRect ? (
    <motion.div
      ref={floatRef}
      className="attr-float"
      style={{
        top:  anchorRect.top,
        left: anchorRect.left,
        width: Math.min(380, window.innerWidth - anchorRect.left - 16),
      }}
      initial={{ opacity: 0, y: -8, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: -8, scale: 0.97 }}
      transition={{ duration: 0.18, ease: "easeOut" }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <p style={{ fontSize: 13, fontWeight: 800, color: T.text, fontFamily: "'Sora',sans-serif" }}>{label}</p>
        <button
          onClick={() => setOpen(false)}
          style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, display: "flex", alignItems: "center", padding: 2 }}
        >
          <X size={14} />
        </button>
      </div>
      {children}
      <button
        onClick={() => setOpen(false)}
        style={{ marginTop: 12, width: "100%", padding: "9px", borderRadius: 10, border: "none", background: GRAD_SAVE, color: "#fff", fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "'Sora',sans-serif" }}
      >
        Done
      </button>
    </motion.div>
  ) : null;
  return (
    <div ref={anchorRef}>
      {triggerRow}
      {createPortal(
        <AnimatePresence>
          {open && (isMob ? mobileSheet : desktopFloat)}
        </AnimatePresence>,
        document.body
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   sphere3D
═══════════════════════════════════════════════════════════════ */
function sphere3D(hex: string): string {
  return [
    `radial-gradient(circle at 35% 32%,`,
    `  rgba(255,255,255,0.55) 0%,`,
    `  rgba(255,255,255,0.10) 28%,`,
    `  transparent 52%),`,
    `radial-gradient(circle at 68% 72%,`,
    `  rgba(0,0,0,0.30) 0%,`,
    `  transparent 55%),`,
    `radial-gradient(circle at 50% 50%,`,
    `  ${hex} 0%,`,
    `  ${hex} 100%)`,
  ].join(" ");
}

function resolveElectronicsDeviceId(subcategoryName: string | undefined): number | null {
  if (!subcategoryName) return null;
  const n = subcategoryName.toLowerCase();
  if (n.includes("tv") || n.includes("television")) return ELECTRONICS_CATEGORY_IDS.TV;
  if (n.includes("phone") || n.includes("smartphone")) return ELECTRONICS_CATEGORY_IDS.PHONE;
  if (n.includes("laptop") || n.includes("notebook")) return ELECTRONICS_CATEGORY_IDS.LAPTOP;
  if (n.includes("tablet")) return ELECTRONICS_CATEGORY_IDS.TABLET;
  if (n.includes("watch")) return ELECTRONICS_CATEGORY_IDS.SMARTWATCH;
  if (n.includes("camera")) return ELECTRONICS_CATEGORY_IDS.CAMERA;
  if (n.includes("headphone") || n.includes("earbud") || n.includes("earphone")) return ELECTRONICS_CATEGORY_IDS.HEADPHONES;
  if (n.includes("console") || n.includes("gaming")) return ELECTRONICS_CATEGORY_IDS.GAMING_CONSOLE;
  return null;
}

function isAttrApplicable(group: AttributeItem, subcategoryId: number | null): boolean {
  const scopedTo = (group as any).appliesTo as number[] | undefined;
  if (!scopedTo) return true;
  return subcategoryId != null && scopedTo.includes(subcategoryId);
}

/* ═══════════════════════════════════════════════════════════════
   GeneralColorPicker
═══════════════════════════════════════════════════════════════ */
const GeneralColorPicker: React.FC<{
  selected: number[];
  onToggle: (id: number) => void;
}> = ({ selected, onToggle }) => {
  const colorGroup = resolveColorGroup();
  if (!colorGroup?.values) return null;
  return (
    <div className="color-grid">
      {colorGroup.values.map(color => {
        const isSel        = selected.includes(color.id);
        const hex          = color.hexCodes?.[0];
        const isMulti      = color.name === "Multicolor";
        const isTransparent = color.name === "Transparent";
        const isLight = hex && ["#FFFFFF","#FFFFF0","#FFFDD0","#FAFAFA","#E6E6FA",
          "#F5F5DC","#87CEEB","#98FF98","#CDDC39","#FFEB3B","#FFC107","#FFDB58"].includes(hex.toUpperCase());
        return (
          <button
            key={color.id}
            className={`color-cell${isSel ? " sel" : ""}`}
            onClick={() => onToggle(color.id)}
            style={{ background: "none", border: "none", padding: 0 }}
          >
            <div style={{ position: "relative", width: 40, height: 40, flexShrink: 0 }}>
              {isMulti ? (
                <div
                  className={`color-sphere color-sphere-multi${isSel ? " sel" : ""}`}
                  style={{ boxShadow: isSel
                    ? "inset -6px -4px 12px rgba(0,0,0,.28),inset 6px 5px 12px rgba(255,255,255,.18),0 0 0 2.5px #C8202E,0 4px 14px rgba(0,0,0,.22)"
                    : undefined
                  }}
                />
              ) : isTransparent ? (
                <div className={`color-sphere color-sphere-transparent${isSel ? " sel" : ""}`} />
              ) : hex ? (
                <div
                  className={`color-sphere${isSel ? " sel" : ""}`}
                  style={{
                    background: sphere3D(hex),
                    boxShadow: isLight
                      ? `inset -5px -4px 10px rgba(0,0,0,.12),inset 4px 4px 8px rgba(255,255,255,.6),0 1px 0 rgba(0,0,0,.15),0 4px 12px rgba(0,0,0,.12)${isSel ? ",0 0 0 2.5px #C8202E" : ""}`
                      : `inset -6px -5px 12px rgba(0,0,0,.30),inset 5px 5px 10px rgba(255,255,255,.18),0 5px 18px rgba(0,0,0,.20)${isSel ? ",0 0 0 2.5px #C8202E" : ""}`,
                  }}
                />
              ) : (
                <div className={`color-sphere${isSel ? " sel" : ""}`} style={{ background: sphere3D(T.bgMuted) }} />
              )}
              {isSel && (
                <div className="color-tick">
                  <Check size={9} color="#fff" strokeWidth={3} />
                </div>
              )}
            </div>
            <span className="color-name">{color.name}</span>
          </button>
        );
      })}
    </div>
  );
};

/* ── Toast ── */
const Toast: React.FC<{ visible: boolean; msg: string; type: "ok" | "err" | "warn" }> = ({ visible, msg, type }) => (
  <AnimatePresence>
    {visible && (
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 20 }}
        style={{
          position: "fixed", bottom: 32, left: "50%", transform: "translateX(-50%)",
          background: type === "ok" ? GRAD_SAVE : type === "err" ? "#C8202E" : T.gold,
          color: "#fff", fontSize: 13, fontWeight: 700, letterSpacing: "0.06em",
          padding: "13px 28px", borderRadius: 999, zIndex: 99999,
          boxShadow: "0 8px 32px rgba(27,14,6,.18)", whiteSpace: "nowrap",
          fontFamily: "'Sora',sans-serif", display: "flex", alignItems: "center", gap: 8,
        }}
      >
        {type === "ok" ? <Check size={14} /> : <AlertCircle size={14} />}
        {msg}
      </motion.div>
    )}
  </AnimatePresence>
);

/* ── Variant Card ── */
const VariantCard: React.FC<{
  colorId:   number;
  colorName: string;
  hex?:      string;
  variant:   VariantShape | undefined;
  stock:     number;
  showSizes: boolean;
  sizeOptions?: { id: number; name: string }[];
  onUpdate:  (id: number, p: Partial<VariantShape>) => void;
  onRemove:  (id: number) => void;
}> = ({ colorId, colorName, hex, variant, stock, showSizes, sizeOptions, onUpdate, onRemove }) => {
  const fileRef = useRef<HTMLInputElement>(null);
  const preview = variant?.previewUrl ?? variant?.imageUrl;
  return (
    <motion.div className="variant-card" initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          {hex && (
            <div style={{
              width: 14, height: 14, borderRadius: "50%", background: hex,
              border: `1.5px solid rgba(0,0,0,0.12)`,
              boxShadow: "inset 0 1px 2px rgba(255,255,255,.3)",
            }} />
          )}
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: "0.1em", color: T.dark, fontFamily: "'Sora',sans-serif" }}>{colorName.toUpperCase()}</span>
        </div>
        <button onClick={() => onRemove(colorId)} style={{ background: "none", border: "none", cursor: "pointer", color: T.muted, padding: 4 }}>
          <X size={14} />
        </button>
      </div>
      <div style={{ display: "flex", gap: 12 }}>
        <div
          onClick={() => fileRef.current?.click()}
          style={{
            width: 80, height: 95, borderRadius: 10, background: T.bgMuted,
            border: `1.5px dashed rgba(200,32,46,.4)`,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
            cursor: "pointer", overflow: "hidden", flexShrink: 0, gap: 4,
          }}
        >
          {preview
            ? <img src={preview} alt={colorName} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            : <><Camera size={16} color={T.orange} /><span style={{ fontSize: 9, color: T.muted, letterSpacing: "0.1em", fontWeight: 700, fontFamily: "'Sora',sans-serif" }}>PHOTO</span></>
          }
          <input ref={fileRef} type="file" hidden accept="image/*" onChange={e => {
            const f = e.target.files?.[0];
            if (!f) return;
            onUpdate(colorId, { imageFile: f, previewUrl: URL.createObjectURL(f) });
          }} />
        </div>
        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
          <div>
            <FL l="Stock" />
            <NIn value={variant?.stock ?? stock ?? 0} onChange={v => onUpdate(colorId, { stock: +v })} type="number" placeholder="0" />
          </div>
          {showSizes && sizeOptions && (
            <div>
              <FL l="Sizes" />
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                {sizeOptions.map(s => {
                  const on = (variant?.sizeIds ?? []).includes(s.id);
                  return (
                    <button key={s.id} className={`ac${on ? " on" : ""}`} style={{ fontSize: 11, padding: "4px 10px" }}
                      onClick={() => onUpdate(colorId, { sizeIds: toggleMulti(variant?.sizeIds ?? [], s.id) })}>
                      {s.name}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

/* ── Generic multi/single-select attribute chip row ── */
const AttrChipRow: React.FC<{
  group: AttributeItem;
  current: number | number[] | undefined;
  onChange: (v: number | number[]) => void;
}> = ({ group, current, onChange }) => {
  const isMulti = !!(group as any).multiSelect;
  return (
    <div style={{ display: "flex", flexWrap: "wrap", gap: 8, paddingTop: 8 }}>
      {(group.values ?? []).map((v: any) => {
        const on = isMulti
          ? Array.isArray(current) && (current as number[]).includes(v.id)
          : current === v.id;
        return (
          <button key={v.id} className={`ac${on ? " on" : ""}`}
            onClick={() => onChange(isMulti ? toggleMulti(Array.isArray(current) ? current as number[] : [], v.id) : v.id)}>
            {v.name}
          </button>
        );
      })}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
═══════════════════════════════════════════════════════════════ */
const AddProducts: React.FC = () => {
  const dispatch   = useDispatch<AppDispatch>();
  const navigate   = useNavigate();
  const { listingId: pid } = useParams<{ listingId?: string }>();
  const userId         = useSelector((s: RootState) => s.auth.user?.userId);
  const partners       = useSelector((s: RootState) => s.commercePartners.partners);
  const partnerLoading = useSelector((s: RootState) => s.commercePartners.loading);
  const currentProduct = useSelector((s: RootState) => s.products.currentProduct);

  // Stores lives under Shopping (categoryId 1) / Stores (subcategoryId 112) —
  // see businessSubcategories in PartnerCategories.tsx: { id:112, parentId:1,
  // name:"Stores" }. This screen should only ever look at the commerce
  // partner brand registered under that exact combination, not just
  // whichever partner doc happens to come back first.
  const currentPartner = (partners as any[])?.find(
    (p) => Number(p.categoryId) === 1 && Number(p.subcategoryId) === 112
  );

  const lockedGroupName = React.useMemo(() => {
    if (!currentPartner) return null;
    // storeType (id 112's own subcategories, e.g. Supermarket/Pharmacy/...)
    // is stored separately from the top-level categoryId/subcategoryId that
    // identify this as a "Stores" brand in the first place — see storeType
    // in PartnerCategories.tsx (ids 11201-11223, parentId 112).
    const storeTypeId = (currentPartner as any).storeTypeId ?? (currentPartner as any).subcategoryId;
    return STORE_TYPE_TO_GROUP[storeTypeId] ?? null;
  }, [currentPartner]);

  const [form,   setForm]   = useState<FormState>(blank());
  const [saving, setSaving] = useState(false);
  const [dirty,  setDirty]  = useState(false);
  const [isMob,  setIsMob]  = useState(window.innerWidth < 900);
  const [snack,  setSnack]  = useState<{ open: boolean; msg: string; type: "ok" | "err" | "warn" }>({ open: false, msg: "", type: "ok" });
  const toast = (msg: string, type: "ok" | "err" | "warn" = "ok") => {
    setSnack({ open: true, msg, type });
    setTimeout(() => setSnack(s => ({ ...s, open: false })), 3000);
  };
  useEffect(() => {
    const fn = () => setIsMob(window.innerWidth < 900);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);
  useEffect(() => { if (userId) dispatch(listenToUserCommercePartners(userId) as any); }, [userId, dispatch]);
  useEffect(() => {
    if (!userId || partnerLoading) return;
    if (partners?.length === 0) navigate(`/CommerceOnboarding/${userId}`, { replace: true });
  }, [partners, partnerLoading, userId, navigate]);
  useEffect(() => { if (pid) dispatch(listenToProductData(pid)); }, [dispatch, pid]);
  useEffect(() => {
    if (!pid || !currentProduct) return;
    setForm({
      listingId:     currentProduct.listingId,
      groupIndex:    findGroupIndex(currentProduct.categoryId ?? null),
      categoryId:    currentProduct.categoryId ?? null,
      subcategoryId: (currentProduct as any).subcategoryId ?? null,
      productId:     currentProduct.productId ?? null,
      title:         currentProduct.name ?? "",
      description:   (currentProduct as any).description ?? "",
      stock:         currentProduct.stock ?? 0,
      rate:          currentProduct.rate ?? 0,
      available:     currentProduct.available ?? true,
      isPublished:   currentProduct.isPublished ?? false,
      images: [], photos: currentProduct.photos ?? [],
      video: currentProduct.videoUrl ?? null, videoFile: null,
      attributes:  (currentProduct as any).attributes ?? {},
      dimensions:  (currentProduct as any).dimensions ?? { unit: "cm", weightUnit: "g" },
      variants:    currentProduct.variants
        ? Object.fromEntries(Object.entries(currentProduct.variants).map(([k, v]: any) => [
            Number(k), { imageUrl: v.imageUrl, imageFile: undefined, previewUrl: undefined, sizeIds: v.sizeIds ?? [], stock: v.stock ?? 0 }
          ]))
        : {},
    });
    setDirty(false);
  }, [currentProduct, pid]);
  const up = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm(p => ({ ...p, [key]: value }));
    setDirty(true);
  };

  /* ── derived ── */
  const group     = form.groupIndex !== null ? CATEGORY_GROUPS[form.groupIndex] : null;
  const groupName = group?.name ?? "";
  const isElectronics = groupName === "Electronics";
  const isFashion     = groupName === "Fashion";
  const isBeauty      = groupName === "Beauty";
  const isMaternity   = groupName === "Maternity";
  const isFarm        = groupName === "Farm";
  const isGifts       = groupName === "Gifts";
  const isGrocery     = groupName === "Grocery";
  const isGeneral     = !!groupName;

  const domainIconRenderer = isFarm ? renderFarmIcon : isGifts ? renderGiftsIcon : isGrocery ? renderGroceryIcon : undefined;

  const visibleCategories = group ? (group.categories as any[]) : [];
  const subs      = group ? (group.subs as any[]).filter((s: any) => s.parentId === form.categoryId) : [];
  const prods     = group ? (group.products as any[]).filter((p: any) => p.parentId === form.subcategoryId) : [];
  const selectedElectronicsSubName = isElectronics
    ? (group?.subs as any[])?.find((s: any) => s.id === form.subcategoryId)?.name
    : undefined;
  const electronicsDeviceId = isElectronics
    ? resolveElectronicsDeviceId(selectedElectronicsSubName)
    : null;

  const genColors: number[] = Array.isArray(form.attributes?.[GENERAL_COLOR_ATTRIBUTE_ID])
    ? form.attributes![GENERAL_COLOR_ATTRIBUTE_ID] as number[]
    : [];
  const hasVariants = genColors.length > 0;

  const updateVariant = (colorId: number, patch: Partial<VariantShape>) => {
    const ex = form.variants?.[colorId];
    up("variants", { ...form.variants, [colorId]: { ...(ex ?? { sizeIds: [], stock: 0 }), ...patch } });
  };
  const removeVariant = (colorId: number) => {
    const nv = { ...form.variants }; delete nv[colorId]; up("variants", nv);
  };

  const handleImages = (files: FileList | null) => {
    if (!files) return;
    const remaining = MAX_IMAGES - form.photos.length - form.images.length;
    if (remaining <= 0) { toast("Maximum 10 photos allowed", "warn"); return; }
    up("images", [...form.images, ...Array.from(files).slice(0, remaining)]);
  };

  const handleSave = async () => {
    if (!form.title.trim()) { toast("Title is required", "warn"); return; }
    setSaving(true);
    const cleanAttr: Record<number, number | number[]> = Object.fromEntries(
      Object.entries(form.attributes ?? {}).filter(([, v]) => v !== undefined).map(([k, v]) => [k, v as number | number[]])
    );
    try {
      const saved = await dispatch(upsertProduct({
        listingId: form.listingId,
        data: {
          name: form.title, description: form.description,
          categoryId: form.categoryId, subcategoryId: form.subcategoryId, productId: form.productId,
          rate: form.rate, available: form.available, isPublished: form.isPublished, isDraft: !form.isPublished,
          attributes: cleanAttr, dimensions: form.dimensions ?? {},
          ...(hasVariants
            ? { variants: Object.fromEntries(Object.entries(form.variants || {}).filter(([, v]) => !!v.imageUrl).map(([k, v]) => [k, { colorId: +k, imageUrl: v.imageUrl!, sizeIds: v.sizeIds ?? [], stock: v.stock ?? 0 }])) }
            : { stock: form.stock }),
        },
      })).unwrap();
      const updatedVariants = { ...form.variants };
      if (hasVariants && form.variants) {
        for (const [cid, vrt] of Object.entries(form.variants)) {
          if (!vrt.imageFile) continue;
          const res = await dispatch(upsertProductMedia({ listingId: saved.listingId, files: [vrt.imageFile], type: "photo" })).unwrap();
          const url = res.downloadURLs?.[0];
          if (url) {
            updatedVariants[Number(cid)] = { ...vrt, imageUrl: url, imageFile: undefined, previewUrl: url };
            updateVariant(Number(cid), { imageUrl: url, imageFile: undefined, previewUrl: url });
          }
        }
        await dispatch(upsertProduct({
          listingId: saved.listingId,
          data: {
            variants: Object.fromEntries(
              Object.entries(updatedVariants)
                .filter(([, v]) => !!v.imageUrl)
                .map(([k, v]) => [k, { colorId: +k, imageUrl: v.imageUrl!, sizeIds: v.sizeIds ?? [], stock: v.stock ?? 0 }])
            ),
          },
        })).unwrap();
      }
      if (form.images.length > 0) await dispatch(upsertProductMedia({ listingId: saved.listingId, files: form.images, type: "photo" })).unwrap();
      if (form.videoFile) await dispatch(upsertProductMedia({ listingId: saved.listingId, files: [form.videoFile], type: "video" })).unwrap();
      toast("Saved successfully ✓");
      setForm(p => ({ ...p, listingId: saved.listingId, images: [], videoFile: null }));
      setDirty(false);
    } catch (err) {
      console.error(err);
      toast("Save failed", "err");
    } finally {
      setSaving(false);
    }
  };
  const handleDelPhoto = (url: string) => {
    if (!form.listingId) return;
    dispatch(deleteProductPhoto({ listingId: form.listingId, photoUrl: url }));
    up("photos", form.photos.filter(p => p !== url));
  };
  const isEdit = !!pid;

  /* ── attribute summaries ── */
  const attrSummary = (attrId: number, allGroups: AttributeItem[]) => {
    const val = form.attributes?.[attrId];
    if (!val) return "";
    const grp = allGroups.find((g) => g.id === attrId);
    if (!grp?.values) return "";
    const ids = Array.isArray(val) ? val : [val];
    return ids.map((id: number) => (grp.values as any[])!.find((v) => v.id === id)?.name ?? "").filter(Boolean).join(", ");
  };
  const genColorSummary = () => {
    if (genColors.length === 0) return "";
    const grp = resolveColorGroup();
    if (!grp?.values) return "";
    return genColors.map(id => grp.values!.find(v => v.id === id)?.name ?? "").filter(Boolean).join(", ");
  };
  const fashionSizes = fashionAttributes.find((g) => g.id === 20001)?.values ?? [];
  const genColorGroup = resolveColorGroup();

  /* ═══════════════════════════════════════════════════
     RENDER
  ═══════════════════════════════════════════════════ */
  return (
    <div style={{ minHeight: "100vh", background: T.pageBg, fontFamily: "'DM Sans',sans-serif", paddingBottom: 100 }}>
      <style>{CSS}</style>

      {/* ── STICKY HEADER ── */}
      <div style={{ background: T.bg, borderBottom: `1px solid ${T.border}`, position: "sticky", top: 0, zIndex: 100, boxShadow: "0 2px 16px rgba(242,169,62,0.08)" }}>
        <div style={{ height: 3, background: GRAD, backgroundSize: GRAD_SIZE, animation: "arSpin 5s linear infinite" }} />
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "13px 20px", maxWidth: 1200, margin: "0 auto" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <button
              onClick={() => navigate(-1)}
              style={{ width: 36, height: 36, border: `1.5px solid ${T.border}`, borderRadius: 10, background: T.bgSoft, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", color: T.muted }}
            >
              <ChevronLeft size={20} />
            </button>
            <div>
              <div style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.22em", color: T.mutedDim, fontFamily: "'Sora',sans-serif" }}>
                {isEdit ? "EDITING LISTING" : "NEW LISTING"}
              </div>
              <h1 style={{ fontWeight: 900, fontSize: isMob ? 17 : 21, color: T.text, letterSpacing: -0.3, lineHeight: 1.15, fontFamily: "'Sora',sans-serif" }}>
                {isEdit ? (form.title || "Edit Product") : "Add Product"}
              </h1>
            </div>
          </div>
          {!isMob && (
            <button
              className={`save-btn${dirty ? " active" : " inactive"}`}
              onClick={dirty && !saving ? handleSave : undefined}
              style={{ width: "auto", padding: "12px 32px", borderRadius: 12, fontSize: 14 }}
              disabled={!dirty || saving}
            >
              {saving ? <><span style={{ animation: "pulse 1s infinite" }}>●</span> Saving…</> : <><Save size={15} /> Save Listing</>}
            </button>
          )}
        </div>
      </div>

      {/* ── BODY ── */}
      <div style={{ maxWidth: 1200, margin: "0 auto", padding: isMob ? "16px 14px" : "24px 28px" }}>
        <div className={isMob ? "" : "ap-desktop-grid"}>

          {/* ════════════════════════════════
              LEFT COLUMN — everything lives in
              ONE Shopify-style form card, split
              into numbered steps, instead of a
              card per micro-section.
          ════════════════════════════════ */}
          <div>
            <div className="ap-form-card">

              {/* ── STEP 1 — CATEGORY ── */}
              <div className="ap-step">
                <StepHead n={1} t="Category" sub="Pick the product's department" />
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                  {CATEGORY_GROUPS.map((g, i) => {
                    const isLocked = !!lockedGroupName && g.name !== lockedGroupName;
                    return (
                      <button
                        key={g.name}
                        className={`gc${form.groupIndex === i ? " on" : ""}`}
                        disabled={isLocked}
                        title={isLocked ? `Your store is registered for ${lockedGroupName} only` : undefined}
                        style={isLocked ? { opacity: 0.35, filter: "grayscale(1)", cursor: "not-allowed" } : undefined}
                        onClick={() => {
                          if (isLocked) return;
                          setForm(p => ({ ...p, groupIndex: i, categoryId: null, subcategoryId: null, productId: null, attributes: {} }));
                          setDirty(true);
                        }}
                      >
                        {GROUP_ICON[g.name] ?? <Package size={15} />}
                        {g.name}
                      </button>
                    );
                  })}
                </div>

                {group && (
                  <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                    <div>
                      <FL l="Category" req />
                      <NSel
                        value={form.categoryId}
                        onChange={v => { setForm(p => ({ ...p, categoryId: v, subcategoryId: null, productId: null })); setDirty(true); }}
                        options={visibleCategories.map((c: any) => ({ id: c.id, name: c.name, icon: c.icon, image: c.image, color: c.color, raw: c }))}
                        placeholder="Select category…"
                      
                      />
                    </div>
                    {subs.length > 0 && (
                      <div>
                        <FL l="Subcategory" req />
                        <NSel
                          value={form.subcategoryId}
                          onChange={v => { setForm(p => ({ ...p, subcategoryId: v, productId: null })); setDirty(true); }}
                          options={subs.map((s: any) => ({ id: s.id, name: s.name, icon: s.icon, image: s.image, color: s.color, raw: s }))}
                          placeholder="Select subcategory…"
                          disabled={!form.categoryId}
                        
                        />
                      </div>
                    )}
                    {prods.length > 0 && (
                      <div>
                        <FL l="Product Type" />
                        <NSel
                          value={form.productId}
                          onChange={v => { up("productId", v); }}
                          options={prods.map((p: any) => ({ id: p.id, name: p.name, icon: p.icon, image: p.image, color: p.color, raw: p }))}
                          placeholder="Select product type…"
                          disabled={!form.subcategoryId}
                      
                        />
                      </div>
                    )}
                  </motion.div>
                )}
              </div>

              {/* ── STEP 2 — DETAILS ── */}
              <div className="ap-step">
                <StepHead n={2} t="Details" sub="Name, description and pricing" />
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  <div>
                    <FL l="Title" req />
                    <NIn value={form.title} onChange={v => up("title", v)} placeholder="Product name…" />
                  </div>
                  <div>
                    <FL l="Description" />
                    <NIn value={form.description} onChange={v => up("description", v)} placeholder="Describe your product…" rows={4} />
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <div>
                      <FL l="Price" req />
                      <NIn value={form.rate} onChange={v => up("rate", +v)} type="number" prefix="€" placeholder="0.00" />
                    </div>
                    {!hasVariants && (
                      <div>
                        <FL l="Stock" />
                        <NIn value={form.stock} onChange={v => up("stock", +v)} type="number" placeholder="0" suffix="units" />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* ── STEP 3 — MEDIA ── */}
              <div className="ap-step">
                <StepHead n={3} t="Media" sub={`${form.photos.length + form.images.length}/${MAX_IMAGES} photos`} />
                <div style={{ display: "flex", gap: 10, overflowX: "auto", paddingBottom: 4 }}>
                  {form.photos.map(url => (
                    <div key={url} className="media-thumb">
                      <img src={url} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <button
                        onClick={() => handleDelPhoto(url)}
                        style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(200,32,46,.85)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <Trash2 size={10} color="#fff" />
                      </button>
                    </div>
                  ))}
                  {form.images.map((f, i) => (
                    <div key={i} className="media-thumb">
                      <img src={URL.createObjectURL(f)} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <button
                        onClick={() => up("images", form.images.filter((_, j) => j !== i))}
                        style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(200,32,46,.85)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <Trash2 size={10} color="#fff" />
                      </button>
                    </div>
                  ))}
                  {(form.photos.length + form.images.length) < MAX_IMAGES && (
                    <label className="add-media">
                      <Upload size={18} color={T.orange} />
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: T.orange, fontFamily: "'Sora',sans-serif" }}>PHOTO</span>
                      <input type="file" hidden multiple accept="image/*" onChange={e => handleImages(e.target.files)} />
                    </label>
                  )}
                  {!form.video && !form.videoFile && (
                    <label className="add-media">
                      <Video size={18} color={T.muted} />
                      <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: "0.1em", color: T.muted, fontFamily: "'Sora',sans-serif" }}>VIDEO</span>
                      <input type="file" hidden accept="video/*" onChange={e => { const f = e.target.files?.[0]; if (f) up("videoFile", f); }} />
                    </label>
                  )}
                  {(form.video || form.videoFile) && (
                    <div className="media-thumb" style={{ width: 82 }}>
                      <video src={form.videoFile ? URL.createObjectURL(form.videoFile) : form.video!} style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                      <button
                        onClick={() => { up("video", null); up("videoFile", null); }}
                        style={{ position: "absolute", top: 4, right: 4, width: 20, height: 20, borderRadius: "50%", background: "rgba(200,32,46,.85)", border: "none", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}
                      >
                        <Trash2 size={10} color="#fff" />
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ── STEP 4 — ATTRIBUTES (only the active vertical's block mounts) ── */}
              {isFashion && (
                <div className="ap-step">
                  <StepHead n={4} t="Attributes" sub="Size, colour & material for this piece" />
                  {fashionAttributes.map((g) => (
                    <AttrSheet key={g.id} label={g.name} optional={(g as any).optional} summary={attrSummary(g.id, fashionAttributes)} disabled={!isAttrApplicable(g, form.subcategoryId)}>
                      {g.id === GENERAL_COLOR_ATTRIBUTE_ID ? (
                        <GeneralColorPicker
                          selected={genColors}
                          onToggle={id => up("attributes", { ...form.attributes, [GENERAL_COLOR_ATTRIBUTE_ID]: toggleMulti(genColors, id) })}
                        />
                      ) : (
                        <AttrChipRow
                          group={g}
                          current={form.attributes?.[g.id]}
                          onChange={v => up("attributes", { ...form.attributes, [g.id]: v })}
                        />
                      )}
                    </AttrSheet>
                  ))}
                </div>
              )}

              {isElectronics && (
                <div className="ap-step">
                  <StepHead n={4} t="Specifications" sub="Device-specific specs plus shared brand/warranty" />
                  {!electronicsDeviceId && (
                    <p style={{ fontSize: 12, color: T.muted, fontFamily: "'DM Sans',sans-serif", marginBottom: 10 }}>
                      Pick a subcategory (TV, Phone, Laptop…) to see its specs.
                    </p>
                  )}
                  <AttrSheet label="Color" summary={genColorSummary() || "Tap to select colors"}>
                    <GeneralColorPicker
                      selected={genColors}
                      onToggle={id => up("attributes", { ...form.attributes, [GENERAL_COLOR_ATTRIBUTE_ID]: toggleMulti(genColors, id) })}
                    />
                  </AttrSheet>
                  {(ELECTRONICS_DEVICE_GROUPS[electronicsDeviceId ?? -1] ?? []).map((g) => (
                    <AttrSheet key={g.id} label={g.name} summary={attrSummary(g.id, [g])}>
                      <AttrChipRow group={g} current={form.attributes?.[g.id]} onChange={v => up("attributes", { ...form.attributes, [g.id]: v })} />
                    </AttrSheet>
                  ))}
                  {commonElectronicsGroups.map((g) => (
                    <AttrSheet key={g.id} label={g.name} summary={attrSummary(g.id, [g])}>
                      <AttrChipRow group={g} current={form.attributes?.[g.id]} onChange={v => up("attributes", { ...form.attributes, [g.id]: v })} />
                    </AttrSheet>
                  ))}
                </div>
              )}

              {isBeauty && (
                <div className="ap-step">
                  <StepHead n={4} t="Attributes" sub="Skin, hair, fragrance and format details" />
                  <AttrSheet label="Color" summary={genColorSummary() || "Tap to select colors"} disabled={!isAttrApplicable({ id: GENERAL_COLOR_ATTRIBUTE_ID } as AttributeItem, form.subcategoryId)}>
                    <GeneralColorPicker
                      selected={genColors}
                      onToggle={id => up("attributes", { ...form.attributes, [GENERAL_COLOR_ATTRIBUTE_ID]: toggleMulti(genColors, id) })}
                    />
                  </AttrSheet>
                  {beautyAttributes.map((g) => (
                    <AttrSheet key={g.id} label={g.name} optional={(g as any).optional} summary={attrSummary(g.id, beautyAttributes)} disabled={!isAttrApplicable(g, form.subcategoryId)}>
                      <AttrChipRow group={g} current={form.attributes?.[g.id]} onChange={v => up("attributes", { ...form.attributes, [g.id]: v })} />
                    </AttrSheet>
                  ))}
                </div>
              )}

              {isMaternity && (
                <div className="ap-step">
                  <StepHead n={4} t="Attributes" sub="Age group for this item" />
                  {maternityAttributeGroups.map((g) => (
                    <AttrSheet key={g.id} label={g.name} summary={attrSummary(g.id, maternityAttributeGroups)} disabled={!isAttrApplicable(g, form.subcategoryId)}>
                      <AttrChipRow group={g} current={form.attributes?.[g.id]} onChange={v => up("attributes", { ...form.attributes, [g.id]: v })} />
                    </AttrSheet>
                  ))}
                </div>
              )}

              {isFarm && (
                <div className="ap-step">
                  <StepHead n={4} t="Farm Details" sub="Unit of sale and quality/variant" />
                  {farmAttributeGroups.map((g) => (
                    <AttrSheet key={g.id} label={g.name} summary={attrSummary(g.id, farmAttributeGroups)} disabled={!isAttrApplicable(g, form.subcategoryId)}>
                      <AttrChipRow group={g} current={form.attributes?.[g.id]} onChange={v => up("attributes", { ...form.attributes, [g.id]: v })} />
                    </AttrSheet>
                  ))}
                </div>
              )}

              {isGifts && (
                <div className="ap-step">
                  <StepHead n={4} t="Gift Details" sub="Occasion and price band" />
                  {giftsAttributeGroups.map((g) => (
                    <AttrSheet key={g.id} label={g.name} summary={attrSummary(g.id, giftsAttributeGroups)} disabled={!isAttrApplicable(g, form.subcategoryId)}>
                      <AttrChipRow group={g} current={form.attributes?.[g.id]} onChange={v => up("attributes", { ...form.attributes, [g.id]: v })} />
                    </AttrSheet>
                  ))}
                </div>
              )}

              {isGrocery && (
                <div className="ap-step">
                  <StepHead n={4} t="Grocery Details" sub="Unit of sale and variant" />
                  {groceryAttributeGroups.map((g) => (
                    <AttrSheet key={g.id} label={g.name} summary={attrSummary(g.id, groceryAttributeGroups)} disabled={!isAttrApplicable(g, form.subcategoryId)}>
                      <AttrChipRow group={g} current={form.attributes?.[g.id]} onChange={v => up("attributes", { ...form.attributes, [g.id]: v })} />
                    </AttrSheet>
                  ))}
                </div>
              )}

              {isGeneral && !isFashion && !isElectronics && !isBeauty && !isMaternity && !isFarm && !isGifts && !isGrocery && (
                <div className="ap-step">
                  <StepHead n={4} t="Attributes" />
                  <AttrSheet
                    label="Color"
                    summary={genColorSummary() || "Tap to select colors"}
                    disabled={!isAttrApplicable({ id: GENERAL_COLOR_ATTRIBUTE_ID } as AttributeItem, form.subcategoryId)}
                  >
                    <GeneralColorPicker
                      selected={genColors}
                      onToggle={id => up("attributes", { ...form.attributes, [GENERAL_COLOR_ATTRIBUTE_ID]: toggleMulti(genColors, id) })}
                    />
                  </AttrSheet>
                </div>
              )}

              {/* ── STEP 5 — VARIANTS (only if colours picked) ── */}
              {hasVariants && (
                <div className="ap-step">
                  <StepHead n={5} t="Colour Variants" sub="Each colour gets its own photo & stock" />
                  <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {genColors.map(cid => {
                      const cv = genColorGroup?.values?.find((v) => v.id === cid);
                      if (!cv) return null;
                      return (
                        <VariantCard
                          key={cid}
                          colorId={cid}
                          colorName={cv.name}
                          hex={cv.hexCodes?.[0]}
                          variant={form.variants?.[cid]}
                          stock={form.stock}
                          showSizes={isFashion}
                          sizeOptions={isFashion ? fashionSizes : undefined}
                          onUpdate={updateVariant}
                          onRemove={removeVariant}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* ── STEP 6 — DIMENSIONS ── */}
              <div className="ap-step">
                <StepHead n={hasVariants ? 6 : 5} t="Dimensions" sub="Helps calculate shipping" />
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <FL l="Unit" />
                      <div style={{ position: "relative" }}>
                        <select
                          className="ap-select"
                          value={form.dimensions?.unit ?? "cm"}
                          onChange={e => up("dimensions", { ...form.dimensions!, unit: e.target.value as "cm" | "inch" })}
                        >
                          <option value="cm">cm</option>
                          <option value="inch">inch</option>
                        </select>
                        <ChevronDown size={13} color={T.muted} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                      </div>
                    </div>
                    <div>
                      <FL l="Weight unit" />
                      <div style={{ position: "relative" }}>
                        <select
                          className="ap-select"
                          value={form.dimensions?.weightUnit ?? "g"}
                          onChange={e => up("dimensions", { ...form.dimensions!, weightUnit: e.target.value as "g" | "ml" })}
                        >
                          <option value="g">g</option>
                          <option value="ml">ml</option>
                        </select>
                        <ChevronDown size={13} color={T.muted} style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", pointerEvents: "none" }} />
                      </div>
                    </div>
                  </div>
                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                    <div>
                      <FL l={`Height (${form.dimensions?.unit ?? "cm"})`} />
                      <NIn value={form.dimensions?.height ?? ""} onChange={v => up("dimensions", { ...form.dimensions!, height: +v })} type="number" placeholder="0" />
                    </div>
                    <div>
                      <FL l={`Width (${form.dimensions?.unit ?? "cm"})`} />
                      <NIn value={form.dimensions?.width ?? ""} onChange={v => up("dimensions", { ...form.dimensions!, width: +v })} type="number" placeholder="0" />
                    </div>
                    <div>
                      <FL l={`Length (${form.dimensions?.unit ?? "cm"})`} />
                      <NIn value={form.dimensions?.length ?? ""} onChange={v => up("dimensions", { ...form.dimensions!, length: +v })} type="number" placeholder="0" />
                    </div>
                    <div>
                      <FL l={`Net weight (${form.dimensions?.weightUnit ?? "g"})`} />
                      <NIn value={form.dimensions?.netWeight ?? ""} onChange={v => up("dimensions", { ...form.dimensions!, netWeight: +v })} type="number" placeholder="0" />
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* ════════════════════════════════
              RIGHT COLUMN — sidebar: one
              Publish card, sticky on desktop
          ════════════════════════════════ */}
          <div className={isMob ? "" : "ap-sidebar-sticky"}>
            <div className="ap-form-card">
              <SLabel t="Publish" />
              <div style={{ marginBottom: 16 }}>
                <div className="vis-row">
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: "'DM Sans',sans-serif" }}>Available</div>
                    <p style={{ fontSize: 11, color: T.muted, marginTop: 2, fontFamily: "'DM Sans',sans-serif" }}>Buyers can view & purchase</p>
                  </div>
                  <Toggle on={form.available} onChange={v => up("available", v)} />
                </div>
                <div className="vis-row">
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 600, color: T.text, fontFamily: "'DM Sans',sans-serif" }}>Published</div>
                    <p style={{ fontSize: 11, color: T.muted, marginTop: 2, fontFamily: "'DM Sans',sans-serif" }}>Visible in marketplace search</p>
                  </div>
                  <Toggle on={form.isPublished} onChange={v => up("isPublished", v)} />
                </div>
              </div>

              <button
                className={`save-btn${dirty ? " active" : " inactive"}`}
                onClick={dirty && !saving ? handleSave : undefined}
                disabled={!dirty || saving}
              >
                {saving
                  ? <><span style={{ animation: "pulse 1s infinite" }}>●</span> Saving…</>
                  : <><Save size={16} /> {isEdit ? "Update Listing" : "Save Listing"}</>
                }
              </button>
              {form.listingId && (
                <p style={{ textAlign: "center", fontSize: 11, color: T.muted, marginTop: 10, fontFamily: "'DM Sans',sans-serif" }}>
                  ID: <span style={{ fontFamily: "monospace", color: T.gold }}>{form.listingId}</span>
                </p>
              )}

              {isEdit && (
                <>
                  <div style={{ height: 1, background: T.border, margin: "20px 0 16px" }} />
                  <SLabel t="Summary" />
                  <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                    {[
                      { label: "Title",  value: form.title || "—" },
                      { label: "Price",  value: form.rate ? `€${form.rate}` : "—" },
                      { label: "Stock",  value: hasVariants ? "Per variant" : String(form.stock) },
                      { label: "Status", value: form.isPublished ? "Published" : "Draft" },
                      { label: "Photos", value: String(form.photos.length + form.images.length) },
                    ].map(row => (
                      <div key={row.label} style={{ display: "flex", justifyContent: "space-between", fontSize: 12, padding: "5px 0", borderBottom: `1px solid ${T.border}` }}>
                        <span style={{ color: T.muted, fontFamily: "'DM Sans',sans-serif" }}>{row.label}</span>
                        <span style={{ color: T.text, fontWeight: 600, fontFamily: "'DM Sans',sans-serif" }}>{row.value}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      {/* ── MOBILE STICKY SAVE ── */}
      {isMob && (
        <div style={{ position: "fixed", bottom: 0, left: 0, right: 0, padding: "12px 16px 20px", background: T.bg, borderTop: `1px solid ${T.border}`, zIndex: 90, boxShadow: "0 -4px 20px rgba(242,169,62,0.1)" }}>
          <button
            className={`save-btn${dirty ? " active" : " inactive"}`}
            onClick={dirty && !saving ? handleSave : undefined}
            disabled={!dirty || saving}
          >
            {saving
              ? <><span style={{ animation: "pulse 1s infinite" }}>●</span> Saving…</>
              : <><Save size={16} /> {isEdit ? "Update Listing" : "Save Listing"}</>
            }
          </button>
        </div>
      )}

      <Toast visible={snack.open} msg={snack.msg} type={snack.type} />
    </div>
  );
};

export default AddProducts;